import {
  EditOutlined,
  FileExcelOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Button,
  Checkbox,
  Col,
  Divider,
  Empty,
  Form,
  Row,
  Space,
  Tabs,
} from "antd";
import dayjs from "dayjs";
import { debounce, isEmpty, isNull } from "lodash";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import {
  listReminders,
  clearSelectedReminderRows,
  setSelectedReminderRows,
  setActiveReminderTab,
  clearReminderProfiles,
} from "../../store/reminder/reminderActions";
import { setTablePreserveOnReload } from "../../store/ui/uiActions";
import { exportDataToExcel } from "../shared/exportToExcel";
import AgencyMultiSelectEx from "../shared/form/AgencyMultiSelectEx";
import DynamicProducerSelect from "../shared/form/DynamicProducerSelect";
import {
  fixedDateRanges,
  dateRangeConfigs,
} from "../shared/form/fixedDateRanges";
import { getInitialAgency } from "../shared/form/getInitialAgency";
import AdvancedRangePicker from "../shared/form/reactHookForm/AdvancedRangePicker";
import { dateFormat, toTZNeutralFormat } from "../shared/formatting";
import { agenciesListEqual } from "../shared/sorting";
import { SearchableTable } from "../shared/table/SearchableTable";
import { tableNames } from "../shared/table/tableNames";
import { manageRemindersColumns } from "./manageRemindersColumns";
import { manageRemindersSchema } from "./manageRemindersSchemas";
import SearchMultiSelectEx from "../shared/form/SearchMultiSelectEx";
import { listReminderProfiles } from "../../store/reminder/reminderActions";
import RenewalRequestStatus from "./RenewalRequestStatus";

//
const initialValues = {
  AgencyKeys: [],
  ExpiryDateFixedDateRange: fixedDateRanges.ALL_DATES,
  ExpiryDate: ["", ""],
  UpdatedDateFixedDateRange: fixedDateRanges.LAST_X_DAYS,
  UpdatedDateNumberOfDays: 7,
  UpdatedDate: ["", ""],
  ShowAutoplanPolicies: true,
  ShowSpecialPolicies: true,
  PolicyStatusActive: true,
  PolicyStatusExpired: true,
  PolicyStatusCancelled: false,
  PolicyStatusRenewed: false,
  PolicyStatusLost: false,
  ProducerCode1Keys: [],
  ProducerCode2Keys: [],
};

const colSize = {
  xs: 24,
  sm: 24,
  md: 24,
  lg: 24,
  xl: 12,
  xxl: 12,
};

const firstLayout = {
  labelCol: {
    xs: null,
    sm: { span: 7 },
    md: { span: 6 },
    lg: { span: 5 },
    xl: { span: 6 },
    xxl: { span: 5 },
  },
  wrapperCol: {
    xs: null,
    sm: { span: 17 },
    md: { span: 18 },
    lg: { span: 19 },
    xl: { span: 18 },
    xxl: { span: 19 },
  },
};

const secondLayout = {
  labelCol: {
    xs: null,
    sm: { span: 7 },
    md: { span: 6 },
    lg: { span: 5 },
    xl: { span: 6 },
    xxl: { span: 5 },
  },
  wrapperCol: {
    xs: null,
    sm: { span: 17 },
    md: { span: 18 },
    lg: { span: 19 },
    xl: { span: 16 },
    xxl: { span: 17 },
  },
};

const ManageRemindersForm = ({ agencies }) => {
  // Form
  const {
    reset,
    control,
    setValue,
    watch,
    trigger,
    getValues,
    formState: { defaultValues, isValid, errors },
  } = useForm({
    mode: "onChange",
    defaultValues: initialValues,
    resolver: yupResolver(manageRemindersSchema),
    reValidateMode: "onChange",
  });

  //
  const dispatch = useDispatch();

  //
  const agencyKeys = watch("AgencyKeys");

  //
  const searchValues = useSelector((state) => state.reminder.searchValues);

  //
  const [currentAgencies, setCurrentAgencies] = useState([]);
  const [viewDetails, setViewDetails] = useState(false);
  const preserveSelectedKeys = useRef(false);
  const updateProducerCodes = useRef(true);

  //
  const homeAgency = useSelector((state) => state.actor.details.data.AgencyKey);
  const loading = useSelector((state) => state.reminder.loading);
  const data = useSelector((state) => state.reminder.data);

  const producerCodes1List = useSelector(
    (state) => state.reminder.producerCodes1List
  );
  const producerCodes2List = useSelector(
    (state) => state.reminder.producerCodes2List
  );
  const producerCodes1Loading = useSelector(
    (state) => state.reminder.producerCodes1Loading
  );
  const producerCodes2Loading = useSelector(
    (state) => state.reminder.producerCodes2Loading
  );
  const selectedRows = useSelector((state) => state.reminder.selectedRows);

  const reminderProfiles = useSelector(
    (state) => state.reminder.reminderProfiles
  );

  //
  const refreshData = async (values = getValues(), preserveKeys = false) => {
    if (preserveKeys === true) {
      preserveSelectedKeys.current = true;
    }

    // Explicitly trigger validation and wait for result
    const isFormValid = await trigger();

    if (dispatch && !isEmpty(values.ReminderProfileIds) && isFormValid) {
      dispatch(listReminders(values, updateProducerCodes.current));
    }
    updateProducerCodes.current = false;
  };

  const debouncedRefreshData = useMemo(() => debounce(refreshData, 1000), []);

  // Replace your local state with Redux state
  const activeTab = useSelector((state) => state.reminder.activeTab);

  // Update tab change handler
  const handleTabChange = (tabKey) => {
    dispatch(setActiveReminderTab(tabKey));
  };

  // Add this function to get filtered data based on active tab
  const getFilteredData = () => {
    if (!data) return [];

    switch (activeTab) {
      case "1": // Waiting For Response
        return data.filter(
          (item) => item.RequestStatus === RenewalRequestStatus.Waiting
        );
      case "2": // Processing
        return data.filter(
          (item) => item.RequestStatus === RenewalRequestStatus.Processing
        );
      case "3": // Waiting On Customer
        return data.filter(
          (item) => item.RequestStatus === RenewalRequestStatus.ResponseSent
        );
      case "4": // Reminders Sent
        return data.filter(
          (item) => item.RequestStatus === RenewalRequestStatus.Reminder
        );
      case "5": // No Phone / Email
        return data.filter(
          (item) => item.RequestStatus === RenewalRequestStatus.NotContacted
        );
      case "6": // Opted Out
        return data.filter(
          (item) => item.RequestStatus === RenewalRequestStatus.Rejected
        );
      case "7": // Resolved
        return data.filter(
          (item) => item.RequestStatus === RenewalRequestStatus.Completed
        );
      case "8": // All
        return data;
      default:
        return [];
    }
  };

  //
  useEffect(() => {
    return () => {
      debouncedRefreshData.cancel();
    };
  }, []);

  useEffect(() => {
    if (!isEmpty(searchValues)) {
      // Reset form with search values
      reset(searchValues);

      // Trigger validation and refresh data
      trigger().then(() => {
        updateProducerCodes.current = true;
        debouncedRefreshData.cancel();
        debouncedRefreshData(searchValues);
      });
    }
  }, []);

  // Then, modify the agencies effect to only reset when needed
  useEffect(() => {
    if (
      agencies != null &&
      !isEmpty(agencies) &&
      !agenciesListEqual(currentAgencies, agencies)
    ) {
      setCurrentAgencies(agencies);
      // Only reset to default if we don't have search values and it's not initial load
      if (isEmpty(searchValues)) {
        resetFieldsToDefault();
      }
    }
  }, [agencies]);

  useEffect(() => {
    if (data != null) {
      if (preserveSelectedKeys.current === true) {
        preserveSelectedKeys.current = false;
      } else {
        dispatch(clearSelectedReminderRows());
      }
    }
  }, [data]);

  //
  const resetFieldsToDefault = () => {
    var initialAgency = getInitialAgency(homeAgency, agencies);

    // Use search values if available, otherwise use defaults
    var newValues = {
      ...(isEmpty(searchValues) ? initialValues : searchValues),
      AgencyKeys: [initialAgency],
      ExpiryDateFixedDateRange: fixedDateRanges.ALL_DATES,
      UpdatedDateFixedDateRange: fixedDateRanges.LAST_X_DAYS,
      UpdatedDateNumberOfDays: 7,
      UpdatedDate: [
        toTZNeutralFormat(dayjs().add(-7, "day").startOf("day")),
        toTZNeutralFormat(dayjs().endOf("day")),
      ],
    };

    reset(newValues);
    trigger();
    debouncedRefreshData.cancel();
    refreshData(newValues);
  };

  //todo review
  const onSelectChange = (selectedRowKeys) => {
    dispatch(setSelectedReminderRows(selectedRowKeys));
  };

  {
    /*  const viewDetailsOnCloseHandler = (shouldReload) => {
    setViewDetails(false);
    if (shouldReload === true) {
      dispatch(setTablePreserveOnReload(tableNames.ManageRenewals, true));
      updateProducerCodes.current = true;
      debouncedRefreshData.cancel();
      refreshData(getValues(), true);
    }
  };

  */
  }

  const tableData = useSelector((state) =>
    state.ui.tables.find((b) => b.name === tableNames.ManageReminders)
  );

  const tableCurrentDataSource = tableData?.extra?.currentDataSource;

  useEffect(() => {
    //if a filter is applied when multiple rows selected, set selectedRows only for the records visible on ui
    let currentDataSourceSelectedRowKeys = [];
    if (
      !isNull(tableCurrentDataSource) &&
      !isEmpty(tableCurrentDataSource) &&
      data?.length !== tableCurrentDataSource?.length
    ) {
      const currentDataSourceRowKeys = tableCurrentDataSource.map((r) => r.Id);
      currentDataSourceSelectedRowKeys = selectedRows.filter((r) =>
        currentDataSourceRowKeys.includes(r)
      );
    }
    dispatch(setSelectedReminderRows(currentDataSourceSelectedRowKeys));
  }, [tableCurrentDataSource, data]);

  useEffect(() => {
    if (!isEmpty(agencyKeys) && !isNull(agencyKeys)) {
      dispatch(listReminderProfiles(agencyKeys));
    }
  }, [agencyKeys]);

  useEffect(() => {
    setValue(
      "ReminderProfileIds",
      reminderProfiles.map((profile) => profile.value)
    );
    updateProducerCodes.current = true;
    debouncedRefreshData();
  }, [reminderProfiles, setValue]);

  const maskSensitiveData = useSelector(
    (state) => state.actor?.details?.data?.MaskSensitiveData
  );

  return (
    <div>
      <Form
        size="small"
        autoComplete="off"
        onFinish={() => {}}
        style={{ marginTop: "16px" }}
      >
        <Row>
          <Col {...colSize}>
            <AgencyMultiSelectEx
              layout={firstLayout}
              formLabel="Agency"
              required={true}
              agencies={currentAgencies}
              isLoading={isEmpty(defaultValues.AgencyKeys)}
              setValue={setValue}
              control={control}
              name="AgencyKeys"
              onChange={(newAgencyKeys) => {
                if (newAgencyKeys != null && newAgencyKeys.length > 0) {
                  updateProducerCodes.current = true;
                  debouncedRefreshData();
                } else {
                  dispatch(clearReminderProfiles());
                  setValue("ProducerCode1Keys", [], {
                    shouldValidate: true,
                    shouldDirty: true,
                  });
                  setValue("ProducerCode2Keys", [], {
                    shouldValidate: true,
                    shouldDirty: true,
                  });
                  setValue("ReminderProfileIds", [], {
                    shouldValidate: true,
                    shouldDirty: true,
                  });
                }
              }}
            />
          </Col>
          <Col {...colSize}>
            <AdvancedRangePicker
              formItem={{
                layout: firstLayout,
                label: "Expiry Date",
              }}
              useDynamicName={true}
              control={control}
              setValue={setValue}
              allowClear={true}
              dateRangeConfig={dateRangeConfigs.NON_TRANSACTION_WITH_ALL}
              name="ExpiryDate"
              format={dateFormat}
              afterChange={() => {
                updateProducerCodes.current = true;
                debouncedRefreshData();
              }}
            />
          </Col>
          <Col {...colSize}>
            <SearchMultiSelectEx
              layout={firstLayout}
              required={true}
              formLabel="Reminder Profile"
              control={control}
              name="ReminderProfileIds"
              data={reminderProfiles}
              setValue={setValue}
              onChange={(newProfileIds) => {
                if (newProfileIds != null && newProfileIds.length > 0) {
                  updateProducerCodes.current = true;
                  debouncedRefreshData();
                }
              }}
            />
          </Col>
          <Col {...colSize}>
            <AdvancedRangePicker
              formItem={{
                layout: firstLayout,
                label: "Updated Date",
              }}
              useDynamicName={true}
              dateRangeConfig={
                dateRangeConfigs.NON_TRANSACTION_NO_FUTURE_WITH_ALL
              }
              control={control}
              setValue={setValue}
              allowClear={true}
              name="UpdatedDate"
              format={dateFormat}
              afterChange={() => {
                updateProducerCodes.current = true;
                debouncedRefreshData();
              }}
            />
          </Col>

          <Col {...colSize}>
            <Form.Item
              {...firstLayout}
              label="Policy Type"
              name="PolicyType"
              required
              valuePropName="checked"
            >
              <Space>
                <Controller
                  control={control}
                  name="ShowAutoplanPolicies"
                  render={({ field }) => (
                    <Checkbox
                      checked={field.value}
                      onChange={(e) => {
                        const currentValues = getValues();
                        const autoplanPolicies = e.target.checked;
                        const specialPolicies =
                          !currentValues.ShowSpecialPolicies &&
                          !e.target.checked
                            ? true
                            : currentValues.ShowSpecialPolicies;

                        setValue("ShowAutoplanPolicies", autoplanPolicies, {
                          shouldValidate: true,
                          shouldDirty: true,
                        });

                        if (
                          currentValues.ShowSpecialPolicies != specialPolicies
                        ) {
                          setValue("ShowSpecialPolicies", specialPolicies, {
                            shouldValidate: true,
                            shouldDirty: true,
                          });
                        }

                        debouncedRefreshData();
                      }}
                      inputRef={field.ref}
                      name="ShowAutoplanPolicies"
                    >
                      Autoplan Policies
                    </Checkbox>
                  )}
                />
                <Controller
                  control={control}
                  name="ShowSpecialPolicies"
                  render={({ field }) => (
                    <Checkbox
                      checked={field.value}
                      name="ShowSpecialPolicies"
                      onChange={(e) => {
                        const currentValues = getValues();
                        const specialPolicies = e.target.checked;
                        const autoplanPolicies =
                          !currentValues.ShowAutoplanPolicies &&
                          !e.target.checked
                            ? true
                            : currentValues.ShowAutoplanPolicies;

                        setValue("ShowSpecialPolicies", specialPolicies, {
                          shouldValidate: true,
                          shouldDirty: true,
                        });

                        if (
                          currentValues.ShowAutoplanPolicies != autoplanPolicies
                        ) {
                          setValue("ShowAutoplanPolicies", autoplanPolicies, {
                            shouldValidate: true,
                            shouldDirty: true,
                          });
                        }

                        debouncedRefreshData();
                      }}
                      inputRef={field.ref}
                    >
                      Special Coverage Policies
                    </Checkbox>
                  )}
                />
              </Space>
            </Form.Item>
          </Col>
          <Col {...colSize}>
            <Form.Item
              {...firstLayout}
              label="Policy Status"
              name="PolicyStatus"
              required
              valuePropName="checked"
            >
              <Space direction="horizontal">
                <Space>
                  <Controller
                    control={control}
                    name="PolicyStatusActive"
                    render={({ field }) => (
                      <Checkbox
                        checked={field.value}
                        onChange={(e) => {
                          const currentValues = getValues();
                          const isChecked = e.target.checked;
                          const isAllPoliciesUnchecked =
                            !isChecked &&
                            !currentValues.PolicyStatusLost &&
                            !currentValues.PolicyStatusCancelled &&
                            !currentValues.PolicyStatusExpired &&
                            !currentValues.PolicyStatusRenewed;

                          if (!isAllPoliciesUnchecked) {
                            setValue("PolicyStatusActive", isChecked, {
                              shouldValidate: true,
                              shouldDirty: true,
                            });

                            debouncedRefreshData();
                          }
                        }}
                        inputRef={field.ref}
                        name="PolicyStatusActive"
                      >
                        Active
                      </Checkbox>
                    )}
                  />
                  <Controller
                    control={control}
                    name="PolicyStatusExpired"
                    render={({ field }) => (
                      <Checkbox
                        checked={field.value}
                        onChange={(e) => {
                          const currentValues = getValues();
                          const isChecked = e.target.checked;
                          const isAllPoliciesUnchecked =
                            !isChecked &&
                            !currentValues.PolicyStatusLost &&
                            !currentValues.PolicyStatusCancelled &&
                            !currentValues.PolicyStatusActive &&
                            !currentValues.PolicyStatusRenewed;

                          if (isAllPoliciesUnchecked) {
                            setValue("PolicyStatusActive", true, {
                              shouldValidate: true,
                              shouldDirty: true,
                            });
                          }
                          setValue("PolicyStatusExpired", isChecked, {
                            shouldValidate: true,
                            shouldDirty: true,
                          });

                          debouncedRefreshData();
                        }}
                        inputRef={field.ref}
                        name="PolicyStatusExpired"
                      >
                        Expired
                      </Checkbox>
                    )}
                  />

                  <Controller
                    control={control}
                    name="PolicyStatusRenewed"
                    render={({ field }) => (
                      <Checkbox
                        checked={field.value}
                        onChange={(e) => {
                          const currentValues = getValues();
                          const isChecked = e.target.checked;
                          const isAllPoliciesUnchecked =
                            !isChecked &&
                            !currentValues.PolicyStatusLost &&
                            !currentValues.PolicyStatusCancelled &&
                            !currentValues.PolicyStatusActive &&
                            !currentValues.PolicyStatusExpired;

                          if (isAllPoliciesUnchecked) {
                            setValue("PolicyStatusActive", true, {
                              shouldValidate: true,
                              shouldDirty: true,
                            });
                          }
                          setValue("PolicyStatusRenewed", isChecked, {
                            shouldValidate: true,
                            shouldDirty: true,
                          });

                          debouncedRefreshData();
                        }}
                        inputRef={field.ref}
                        name="PolicyStatusRenewed"
                      >
                        Renewed
                      </Checkbox>
                    )}
                  />
                  <Controller
                    control={control}
                    name="PolicyStatusCancelled"
                    render={({ field }) => (
                      <Checkbox
                        checked={field.value}
                        onChange={(e) => {
                          const currentValues = getValues();
                          const isChecked = e.target.checked;
                          const isAllPoliciesUnchecked =
                            !isChecked &&
                            !currentValues.PolicyStatusLost &&
                            !currentValues.PolicyStatusExpired &&
                            !currentValues.PolicyStatusActive &&
                            !currentValues.PolicyStatusRenewed;

                          if (isAllPoliciesUnchecked) {
                            setValue("PolicyStatusActive", true, {
                              shouldValidate: true,
                              shouldDirty: true,
                            });
                          }
                          setValue("PolicyStatusCancelled", isChecked, {
                            shouldValidate: true,
                            shouldDirty: true,
                          });

                          debouncedRefreshData();
                        }}
                        inputRef={field.ref}
                        name="PolicyStatusCancelled"
                      >
                        Cancelled
                      </Checkbox>
                    )}
                  />

                  <Controller
                    control={control}
                    name="PolicyStatusLost"
                    render={({ field }) => (
                      <Checkbox
                        checked={field.value}
                        onChange={(e) => {
                          const currentValues = getValues();
                          const isChecked = e.target.checked;
                          const isAllPoliciesUnchecked =
                            !isChecked &&
                            !currentValues.PolicyStatusRenewed &&
                            !currentValues.PolicyStatusCancelled &&
                            !currentValues.PolicyStatusActive &&
                            !currentValues.PolicyStatusExpired;

                          if (isAllPoliciesUnchecked) {
                            setValue("PolicyStatusActive", true, {
                              shouldValidate: true,
                              shouldDirty: true,
                            });
                          }
                          setValue("PolicyStatusLost", isChecked, {
                            shouldValidate: true,
                            shouldDirty: true,
                          });

                          debouncedRefreshData();
                        }}
                        inputRef={field.ref}
                        name="PolicyStatusLost"
                      >
                        Lost
                      </Checkbox>
                    )}
                  />
                </Space>
              </Space>
            </Form.Item>
          </Col>
          <Col {...colSize}>
            <DynamicProducerSelect
              formLayout={secondLayout}
              formLabel="Producer 1"
              name="ProducerCode1Keys"
              setValue={setValue}
              control={control}
              producerCodesList={producerCodes1List}
              isProducerCodesListLoading={producerCodes1Loading}
              afterChange={() => {
                debouncedRefreshData();
              }}
            />
          </Col>
          <Col {...colSize}>
            <DynamicProducerSelect
              formLayout={secondLayout}
              formLabel="Producer 2"
              name="ProducerCode2Keys"
              setValue={setValue}
              control={control}
              producerCodesList={producerCodes2List}
              isProducerCodesListLoading={producerCodes2Loading}
              afterChange={() => {
                debouncedRefreshData();
              }}
            />
          </Col>
        </Row>
      </Form>

      <Row style={{ paddingTop: "16px" }}>
        <Col>
          <div>
            {/* ... existing form code ... */}
            <Row style={{ paddingTop: "16px" }}>
              <Col>
                <Tabs
                  size="small"
                  activeKey={activeTab}
                  onChange={handleTabChange}
                  type="card"
                  items={[
                    { label: "Waiting For Response", key: "1" },
                    { label: "Processing", key: "2" },
                    { label: "Waiting On Customer", key: "3" },
                    { label: "Reminders Sent", key: "4" },
                    { label: "No Phone / Email", key: "5" },
                    { label: "Opted Out From Reminders", key: "6" },
                    { label: "Customer Request Resolved", key: "7" },
                    { label: "All", key: "8" },
                  ]}
                />
                <SearchableTable
                  tableName={tableNames.ManageReminders}
                  keyColumn="RenewalKey"
                  scroll={{ y: 550, x: 2300 }}
                  dataSource={getFilteredData()}
                  columns={manageRemindersColumns()}
                  loading={loading}
                  size="small"
                  buttons={[
                    <Button
                      size={"small"}
                      disabled={!isValid}
                      onClick={() => {
                        updateProducerCodes.current = true;
                        debouncedRefreshData.cancel();
                        refreshData(getValues());
                      }}
                    >
                      <ReloadOutlined />
                      Refresh Table
                    </Button>,
                    <Button
                      type="primary"
                      size={"small"}
                      disabled={true}
                      loading={false}
                      onClick={() => setViewDetails(true)}
                    >
                      <EditOutlined />
                      Bulk Assign
                    </Button>,
                  ]}
                  rowSelection={{
                    columnWidth: 30,
                    selectedRowKeys: selectedRows,
                    onChange: onSelectChange,
                    fixed: true,
                    hideSelectAll: false,
                  }}
                  rowKey={(r) => r.RenewalKey}
                  locale={{
                    emptyText: (
                      <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description={"No data"}
                      />
                    ),
                  }}
                />
              </Col>
            </Row>
          </div>
        </Col>
      </Row>
    </div>
  );
};
export default ManageRemindersForm;
